import { createFileRoute } from "@tanstack/react-router";
import { LocationPageTemplate, type LocationVariant } from "@/components/LocationPageTemplate";
import { getLocationCity } from "@/data/location-cities";
import { canonical } from "@/lib/seo";

const PATH = "/software-integration-montclair-ca";
const TITLE = "Software Integration Montclair CA | SimpleTech Helper – (909) 255-9112";
const DESCRIPTION =
  "Looking for software integration in Montclair, CA? SimpleTech Helper automates workflows and connects your apps. Save 5–20 hrs/week. Call (909) 255-9112.";

export const Route = createFileRoute("/software-integration-montclair-ca")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "software integration Montclair CA, workflow automation Montclair, Zapier Make Power Automate Montclair, app integration Inland Empire, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical(PATH) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Montclair" },
      { name: "geo.position", content: "34.0775;-117.6897" },
      { name: "ICBM", content: "34.0775, -117.6897" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          telephone: "(909) 255-9112",
          url: "https://simpletechhelper.com",
          areaServed: "Montclair CA, Inland Empire",
          serviceType: "Software Integration and Workflow Automation",
        }),
      },
    ],
  }),
  component: SoftwareIntegrationMontclairPage,
});

const FAQS = [
  {
    q: "What is software integration and why does my Montclair business need it?",
    a: "Software integration connects your apps so data moves automatically between them — no manual copy-pasting. Most Montclair small businesses save 5–20 hours per week once key workflows are automated.",
  },
  {
    q: "Which platforms does SimpleTech Helper use for automation?",
    a: "We're platform-neutral — we use Zapier, Make (formerly Integromat), Microsoft Power Automate, n8n, or direct APIs depending on what fits your Montclair business's stack and budget best.",
  },
  {
    q: "Do I need technical knowledge to use the automations you build?",
    a: "No. SimpleTech Helper builds in your accounts and hands over plain-English documentation. Your Montclair team just uses the tools — the automation runs in the background.",
  },
  {
    q: "Can you integrate my specific apps? I use [X] and [Y].",
    a: "Probably yes. We work with 100+ apps across CRM, accounting, marketing, scheduling, and productivity. Call (909) 255-9112 or send us your app list and we'll confirm.",
  },
  {
    q: "Will I own the automations after you build them?",
    a: "Yes. Everything is built in your accounts. You own the workflows, the credentials, and the documentation. No lock-in.",
  },
  {
    q: "How long does it take to build and launch a workflow for a Montclair business?",
    a: "A single Starter workflow typically takes 1–2 weeks from discovery to launch. Multi-workflow Growth packages run 3–6 weeks depending on complexity.",
  },
  {
    q: "Is SimpleTech Helper based in Montclair?",
    a: "Yes. Our office is at 8880 N Benson Ave #121, Montclair, CA 91763. We serve the full Inland Empire but Montclair is home — which means we can meet in person, move fast, and stay accountable.",
  },
];

const INTRO = (
  <>
    <p>
      The average Montclair small business uses 30+ different apps — and most of them don't talk
      to each other. Your team ends up rekeying the same info into your CRM, accounting software,
      email tool, and spreadsheets every single day. That's lost hours, duplicate data, and missed
      follow-ups.
    </p>
    <p>
      <strong className="text-[#0b0a3d]">SimpleTech Helper</strong> delivers{" "}
      <strong className="text-[#0b0a3d]">software integration Montclair CA</strong> businesses
      rely on to stop the manual grind: we connect your tools using Zapier, Make, Microsoft Power
      Automate, and direct APIs, so customer data flows automatically, reports build themselves,
      and your team gets their time back. We're based at{" "}
      <strong className="text-[#0b0a3d]">8880 N Benson Ave #121, Montclair, CA 91763</strong> —
      local, responsive, and fluent in plain English.
    </p>
    <p>
      Ready to stop the manual grind? Call{" "}
      <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
        {"\n"}(909) 255-9112
      </a>{" "}
      for a free automation review.
    </p>
  </>
);

const VARIANT: LocationVariant = {
  serviceLabel: "Software Integration",
  serviceLabelShort: "Software Integration",
  parentHref: "/services/software-integration",
  parentLabel: "Software Integration",
  learnMoreText: "EXPLORE OUR SOFTWARE INTEGRATION SERVICES",
  nearMeEyebrow: "Software Integration Near Me",
  whyTitle: "Why Choose SimpleTech Helper for Software Integration in Montclair",
  expertHeroTitle:
    "Software Integration & Workflow Automation for Montclair, CA Small Businesses",
  expertNearMeTitle: "Trusted Software Integration in Montclair, CA",
  introStrong: "Software Integration Montclair CA",
  introBody: INTRO,
  faqHeading: (
    <>Frequently Asked<br />Questions About<br />Software Integration in Montclair</>
  ),
  ctaTitle: "Ready to Put Your Montclair Business on Autopilot?",
  blogEyebrow: "From the Blog",
  blogTitle: "Automation & Integration Tips for Montclair Businesses",
  faqs: FAQS,
};

function SoftwareIntegrationMontclairPage() {
  const city = getLocationCity("montclair")!;
  return <LocationPageTemplate city={city} variant={VARIANT} />;
}
